'use client'

import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useLoginState } from '@/hooks/use-login-state'
import type { Volunteer } from '@/payload-types'

type EventVolunteerCTAProps = {
  eventId: string
  basePath?: string
}

type VolunteerRowStatus = NonNullable<Volunteer['status']>

const APP_HOST = process.env.NEXT_PUBLIC_APP_HOST

function getAppHref(path: `/${string}`): string {
  if (!APP_HOST) {
    return path.startsWith('/app/') || path === '/app' ? path : `/app${path}`
  }

  const protocol = APP_HOST.startsWith('localhost') || APP_HOST.startsWith('app.localhost') ? 'http' : 'https'
  return `${protocol}://${APP_HOST}${path}`
}

function getCurrentSearch(): string {
  if (typeof window === 'undefined') return ''
  return window.location.search || ''
}

function buildSignUpRedirectHref(basePath: string, eventId: string): string {
  const redirectTarget = `${basePath}/${eventId}${getCurrentSearch()}`
  const params = new URLSearchParams({ redirect: redirectTarget })
  return getAppHref(`/sign-up?${params.toString()}`)
}

function isBackOutStatus(rowStatus: VolunteerRowStatus | null): boolean {
  return (
    rowStatus === 'Confirmed' ||
    rowStatus === 'Approved' ||
    rowStatus === 'Waitlisted' ||
    rowStatus === 'Pending'
  )
}

function isConfirmedStatus(rowStatus: VolunteerRowStatus | null): boolean {
  return rowStatus === 'Confirmed' || rowStatus === 'Approved'
}

export function EventVolunteerCTA({ eventId, basePath = '/events' }: EventVolunteerCTAProps) {
  const { isLoading: loginLoading, isLoggedIn } = useLoginState()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rowStatus, setRowStatus] = useState<VolunteerRowStatus | null | undefined>(undefined)
  const [eventFull, setEventFull] = useState(false)
  const [profileErrorOpen, setProfileErrorOpen] = useState(false)
  const [confirmBackOutOpen, setConfirmBackOutOpen] = useState(false)

  useEffect(() => {
    if (loginLoading) return

    let cancelled = false
    setRowStatus(undefined)

    void (async () => {
      try {
        const response = await fetch(
          `/api/event-volunteer?eventId=${encodeURIComponent(eventId)}`,
          { method: 'GET', credentials: 'include' },
        )

        if (cancelled) return

        const payload = (await response.json().catch(() => ({}))) as {
          status?: VolunteerRowStatus | null
          eventFull?: boolean
        }
        if (cancelled) return
        setEventFull(Boolean(payload.eventFull))
        setRowStatus((payload.status ?? null) as VolunteerRowStatus | null)
      } catch {
        if (!cancelled) setRowStatus(null)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [loginLoading, isLoggedIn, eventId])

  const isLoading = loginLoading || rowStatus === undefined
  const canBackOut = isBackOutStatus(rowStatus ?? null)

  const ctaLabel = isLoading
    ? 'Loading...'
    : isSubmitting
      ? 'Saving...'
      : canBackOut
        ? 'Back Out'
        : eventFull
          ? 'Join Waitlist'
          : 'Volunteer'

  const handleVolunteer = async () => {
    if (isSubmitting || isLoading || canBackOut) return

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/event-volunteer', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eventId }),
      })

      const payload = (await response.json().catch(() => ({}))) as {
        error?: string
        code?: string
        profileUrl?: string
        status?: VolunteerRowStatus | null
        message?: string
      }

      if (response.status === 401) {
        window.location.assign(buildSignUpRedirectHref(basePath, eventId))
        return
      }

      if (response.status === 409 && payload.status) {
        setRowStatus(payload.status)
        toast.error(payload.error || 'Unable to update volunteer status.')
        return
      }

      if (response.status === 422 && payload.code === 'VOLUNTEER_PROFILE_INCOMPLETE') {
        setProfileErrorOpen(true)
        return
      }

      if (!response.ok) {
        toast.error(payload.error || 'Unable to update volunteer status.')
        return
      }

      const next = payload.status ?? null
      if (next) {
        setRowStatus(next)
      }

      toast.success(payload.message || 'Your volunteer request is pending.')
    } catch {
      toast.error('Unable to update volunteer status.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackOut = async () => {
    if (isSubmitting || isLoading || !canBackOut) return

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/event-volunteer', {
        method: 'DELETE',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ eventId }),
      })

      const payload = (await response.json().catch(() => ({}))) as {
        error?: string
        status?: VolunteerRowStatus | null
        message?: string
      }

      if (response.status === 401) {
        window.location.assign(buildSignUpRedirectHref(basePath, eventId))
        return
      }

      if (!response.ok) {
        toast.error(payload.error || 'Unable to update volunteer status.')
        return
      }

      setRowStatus(payload.status ?? null)
      setConfirmBackOutOpen(false)
      toast.success(payload.message || 'You have backed out of this event.')
    } catch {
      toast.error('Unable to update volunteer status.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleActionClick = () => {
    if (!canBackOut) {
      void handleVolunteer()
      return
    }

    if (isConfirmedStatus(rowStatus ?? null)) {
      setConfirmBackOutOpen(true)
      return
    }

    void handleBackOut()
  }

  return (
    <>
      <Button
        type="button"
        onClick={handleActionClick}
        disabled={isSubmitting || isLoading}
      >
        {ctaLabel}
      </Button>
      <Dialog open={profileErrorOpen} onOpenChange={setProfileErrorOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Complete your profile first</DialogTitle>
            <DialogDescription>
              Before you can volunteer for an event, please complete your volunteer profile.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <p>
              We need your phone number, address, primary metro area, photo release, and terms
              acceptance before adding you to an event.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              onClick={() => {
                setProfileErrorOpen(false)
                window.location.assign(getAppHref('/profile'))
              }}
            >
              Go to profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={confirmBackOutOpen} onOpenChange={setConfirmBackOutOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>You are already confirmed for this show</DialogTitle>
            <DialogDescription>
              Are you sure you want to back out? This will remove you from the volunteer list.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmBackOutOpen(false)}
              disabled={isSubmitting}
            >
              Keep my spot
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleBackOut()}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Backing out...' : 'Back out'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
